import type { HTMLAttributes, ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export type StatProps = HTMLAttributes<HTMLDivElement> & {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  /** Numeric delta; positive/negative colours the arrow. Pass `deltaLabel` for the text. */
  delta?: number;
  deltaLabel?: ReactNode;
  /** When true, a negative delta is good (e.g. faster pace). */
  invertDelta?: boolean;
  sub?: ReactNode;
  icon?: ReactNode;
  size?: "md" | "lg";
};

export function Stat({ label, value, unit, delta, deltaLabel, invertDelta, sub, icon, size = "md", className, ...props }: StatProps) {
  const good = delta === undefined ? undefined : invertDelta ? delta < 0 : delta > 0;
  const neutral = delta === 0;
  return (
    <div className={cn("flex flex-col gap-1 rounded-lg border border-border bg-surface p-4 shadow-card", className)} {...props}>
      <div className="flex items-center justify-between gap-2 text-xs font-medium uppercase tracking-wide text-fg-muted">
        <span>{label}</span>
        {icon && <span className="text-fg-muted [&_svg]:size-4">{icon}</span>}
      </div>
      <div className="flex items-baseline gap-1">
        <span className={cn("num font-semibold leading-none tracking-tight", size === "lg" ? "text-4xl" : "text-2xl")}>{value}</span>
        {unit && <span className="text-sm text-fg-muted">{unit}</span>}
      </div>
      {(delta !== undefined || sub) && (
        <div className="flex items-center gap-2 text-xs">
          {delta !== undefined && (
            <span className={cn("inline-flex items-center gap-0.5 font-medium num", neutral ? "text-fg-muted" : good ? "text-success" : "text-danger")}>
              {!neutral && (delta > 0 ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />)}
              {deltaLabel ?? `${delta > 0 ? "+" : ""}${delta}`}
            </span>
          )}
          {sub && <span className="text-fg-muted">{sub}</span>}
        </div>
      )}
    </div>
  );
}
